import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  File,
  ArrowLeft,
  Edit3,
  Trash2,
  Check,
  X,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { addSegmentToDocument, deleteBulkSegments, getDocumentDetail, updateSegment } from 'slices/knowledge/thunk';
import EditChunkModal from './components/EditChunkModal';

const DocumentDetail = () => {
  const { knowledgeId, documentId } = useParams<{ knowledgeId: string; documentId: string }>();
  const navigate = useNavigate();

  // State
  const [selectedDocument, setSelectedDocument] = useState<any | null>(null);
  const [questionAnswers, setQuestionAnswers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedQuestionAnswers, setSelectedQuestionAnswers] = useState<string[]>([]);
  const [editingQuestionAnswer, setEditingQuestionAnswer] = useState<any | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const dispatch = useDispatch<any>();
  const documentDetail = useSelector((state: any) => state.Knowledge.documentDetail);

  // Load document details and segments
  useEffect(() => {
    if (!documentId) {
      navigate(`/knowledge/${knowledgeId}/documents`);
      return;
    }

    setLoading(true);
    dispatch(getDocumentDetail(documentId));
  }, [documentId]);

  useEffect(() => {
    if (documentDetail && documentDetail.id === documentId) {
      setSelectedDocument(documentDetail);
      setQuestionAnswers([...(documentDetail.knowledgeBaseVectors || [])].sort((a: any, b: any) => (a.position || 0) - (b.position || 0)));
      setLoading(false);
    }
  }, [documentDetail]);

  // Segment selection handlers
  const handleSelectAllSegments = (checked: boolean) => {
    if (checked) {
      setSelectedQuestionAnswers(questionAnswers.map(qa => qa.id));
    } else {
      setSelectedQuestionAnswers([]);
    }
  };

  const handleSelectSegment = (segmentId: string, checked: boolean) => {
    if (checked) {
      setSelectedQuestionAnswers([...selectedQuestionAnswers, segmentId]);
    } else {
      setSelectedQuestionAnswers(selectedQuestionAnswers.filter(id => id !== segmentId));
    }
  };

  const handleToggleSegment = (segmentId: string) => {
    setQuestionAnswers(qas =>
      qas.map(qa =>
        qa.id === segmentId
          ? { ...qa, enabled: !qa.enabled }
          : qa
      )
    );
  };

  const handleEnableSelected = () => {
    setQuestionAnswers(qas =>
      qas.map(qa =>
        selectedQuestionAnswers.includes(qa.id)
          ? { ...qa, enabled: true }
          : qa
      )
    );
  };

  const handleDisableSelected = () => {
    setQuestionAnswers(qas =>
      qas.map(qa =>
        selectedQuestionAnswers.includes(qa.id)
          ? { ...qa, enabled: false }
          : qa
      )
    );
  };

  const handleDeleteSelected = () => {
    // Dispatch API to delete selected segments
    dispatch(deleteBulkSegments({ knowledgeId: knowledgeId!, documentId: documentId!, segmentIds: selectedQuestionAnswers }));
    setSelectedQuestionAnswers([]);
  };

  const handleDeleteSegment = (segmentId: string) => {
    // Dispatch API to delete single segment
    dispatch(deleteBulkSegments({ knowledgeId: knowledgeId!, documentId: documentId!, segmentIds: [segmentId] }));
    setSelectedQuestionAnswers(sel => sel.filter(id => id !== segmentId));
  };

  const handleEditSegment = (segment: any) => {
    setEditingQuestionAnswer(segment);
    setEditModalOpen(true);
  };

  const handleSaveSegment = async (updatedSegment: any) => {
    if (!!updatedSegment?.id) {
      await dispatch(updateSegment({ knowledgeId: knowledgeId!, documentId: documentId!, segmentId: updatedSegment.id, payload: updatedSegment }));
    } else {
      await dispatch(addSegmentToDocument({ knowledgeId: knowledgeId!, documentId: documentId!, payload: updatedSegment }));
    }

    setEditModalOpen(false);
    setEditingQuestionAnswer(null);
  };

  const handleAddChunk = () => {
    // Create a new empty segment for adding
    const newSegment = {
      position: questionAnswers.length + 1,
      content: '',
      keywords: [],
    };

    setEditingQuestionAnswer(newSegment);
    setEditModalOpen(true);
  };

  // Filtered segments (search)
  const filteredSegments = questionAnswers.filter(seg =>
    !searchQuery || seg.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="h-[calc(100vh-70px)] bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-sm text-gray-600">Loading document details...</p>
        </div>
      </div>
    );
  }

  if (!selectedDocument) {
    return (
      <div className="h-[calc(100vh-70px)] bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-gray-600">Document not found</p>
          <button
            onClick={() => navigate(`/knowledge/${knowledgeId}/documents`)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Documents
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-70px)] bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-3 py-3 sm:px-6 sm:py-[22px]">
        <div className="flex flex-col gap-3 sm:gap-0 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <button
              onClick={() => navigate(`/knowledge/${knowledgeId}/documents`)}
              className="flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Back</span>
            </button>

            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                <File className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
              </div>
              <div className="flex items-center space-x-1 sm:space-x-2 min-w-0">
                <h1 className="text-sm sm:text-base font-medium text-gray-900 truncate">{selectedDocument.name}</h1>
                <button className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0">
                </button>
              </div>
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium whitespace-nowrap">GENERAL</span>
            </div>
          </div>

          <div className="flex sm:items-center justify-between sm:justify-end items-end space-x-2 sm:space-x-3 ms-auto sm:ms-none">
            <div className="flex items-center space-x-2">
              <button onClick={handleAddChunk} className="flex items-center space-x-1 sm:space-x-2 px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-lg text-xs sm:text-sm text-gray-700 hover:bg-gray-50">
                <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="">Add Chunk</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-3 sm:p-6 bg-white">
        {/* Show skeleton/fuzzy state when document is being processed */}
        {documentDetail?.status === 'Queuing' || selectedDocument?.status === 'Queuing' ? (
          <div className="flex space-x-6">
            {/* Left side - Skeleton Chunks */}
            <div className="flex-1 w-[100vh]">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4">
                <div className="flex items-center space-x-2 sm:space-x-4 order-2 sm:order-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-900">-- CHUNKS</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2 sm:space-x-3 order-1 sm:order-2">
                  <div className="relative flex-1 sm:flex-initial">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full sm:w-auto pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Skeleton Chunks List */}
              <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden opacity-60">
                {[...Array(4)].map((_, index) => (
                  <div key={index} className="group/segment relative flex items-start px-3 py-3 sm:px-4 sm:py-4">
                    {/* Selection Checkbox Skeleton */}
                    <div className="pt-0.5 mr-2 sm:mr-3">
                      <div className="w-4 h-4 bg-gray-300 rounded animate-pulse"></div>
                    </div>

                    {/* Content Skeleton */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-1">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                          <div className="h-3 w-16 bg-gray-300 rounded animate-pulse"></div>
                          <div className="h-3 w-12 bg-gray-300 rounded animate-pulse"></div>
                          <div className="h-3 w-14 bg-gray-300 rounded animate-pulse"></div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-gray-300 rounded animate-pulse"></div>
                          <div className="w-6 h-6 bg-gray-300 rounded animate-pulse"></div>
                          <div className="w-8 h-4 bg-gray-300 rounded-full animate-pulse"></div>
                        </div>
                      </div>

                      <div className="space-y-2 mb-3">
                        <div className="h-4 bg-gray-300 rounded animate-pulse w-full"></div>
                        <div className="h-4 bg-gray-300 rounded animate-pulse w-5/6"></div>
                        <div className="h-4 bg-gray-300 rounded animate-pulse w-4/5"></div>
                        {index % 2 === 0 && <div className="h-4 bg-gray-300 rounded animate-pulse w-3/4"></div>}
                      </div>

                      {/* Keywords skeleton */}
                      <div className="flex flex-wrap gap-1">
                        {[...Array(Math.floor(Math.random() * 4) + 2)].map((_, keyIndex) => (
                          <div key={keyIndex} className="h-5 bg-gray-300 rounded animate-pulse" style={{ width: `${Math.random() * 40 + 30}px` }}></div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex space-x-6">
            {/* Left side - Chunks */}
            <div className="flex-1 w-[100vh]">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4">
                <div className="flex items-center space-x-2 sm:space-x-4 order-2 sm:order-1">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedQuestionAnswers.length === questionAnswers.length && questionAnswers.length > 0}
                      onChange={(e) => handleSelectAllSegments(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm font-medium text-gray-900">
                      {questionAnswers.length} CHUNKS
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2 sm:space-x-3 order-1 sm:order-2">
                  <div className="relative flex-1 sm:flex-initial">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full sm:w-auto pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Selection Actions Bar */}
              {selectedQuestionAnswers.length > 0 && (
                <div className="bg-blue-600 text-white rounded-lg p-3 mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:justify-between">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                    <span className="font-medium text-sm sm:text-base">{selectedQuestionAnswers.length} Selected</span>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={handleEnableSelected}
                        className="flex items-center space-x-1 px-2 py-1 sm:px-3 sm:py-1 bg-white bg-opacity-20 rounded text-xs sm:text-sm hover:bg-opacity-30"
                      >
                        <Check className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>Enable</span>
                      </button>
                      <button
                        onClick={handleDisableSelected}
                        className="flex items-center space-x-1 px-2 py-1 sm:px-3 sm:py-1 bg-white bg-opacity-20 rounded text-xs sm:text-sm hover:bg-opacity-30"
                      >
                        <X className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>Disable</span>
                      </button>
                      <button
                        onClick={handleDeleteSelected}
                        className="flex items-center space-x-1 px-2 py-1 sm:px-3 sm:py-1 bg-red-500 rounded text-xs sm:text-sm hover:bg-red-600"
                      >
                        <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      setSelectedQuestionAnswers([])
                    }
                    className="text-white hover:text-gray-200 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {/* Chunks List */}
              <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
                {filteredSegments.map((segment) => {
                  const isSelected = selectedQuestionAnswers.includes(segment.id);
                  const isEditing = editingQuestionAnswer?.id === segment.id;
                  return (
                    <div
                      key={segment.id}
                      className={`group/segment relative flex items-start px-3 py-3 sm:px-4 sm:py-4 transition-colors ${isEditing
                        ? 'bg-blue-100 border-l-4 border-blue-500'
                        : isSelected
                          ? 'bg-blue-50'
                          : 'hover:bg-gray-50'
                        }`}
                    >
                      {/* Selection Checkbox */}
                      <div className="pt-0.5 mr-2 sm:mr-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleSelectSegment(segment.id, e.target.checked)}
                          className="rounded border-gray-300"
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-1">
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                            <span className="text-[10px] sm:text-[11px] font-medium text-gray-700 tracking-wide">
                              Chunk-{String(segment.position).padStart(2, '0')}
                            </span>
                            <span className="text-[10px] sm:text-[11px] text-gray-500">
                              {segment.wordCount} words
                            </span>
                            <span className={`text-[10px] sm:text-[11px] font-medium ${segment.enabled ? 'text-green-600' : 'text-gray-400'}`}>
                              {segment.enabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>

                          {/* Action buttons (show on hover for desktop, always show on mobile) */}
                          <div className="flex items-center gap-2 opacity-100 sm:opacity-0 sm:group-hover/segment:opacity-100 transition-opacity duration-150">
                            <button
                              onClick={() => handleEditSegment(segment)}
                              className="p-1 sm:p-1.5 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700"
                            >
                              <Edit3 className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSegment(segment.id)}
                              className="flex-1 p-1 sm:p-1.5 rounded hover:bg-red-100 text-gray-500 hover:text-red-600"
                            >
                              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                            <button
                              onClick={() => handleToggleSegment(segment.id)}
                              className={`hidden sm:inline-flex h-3 w-6 sm:h-4 sm:w-8 items-center rounded-full transition-colors ${segment.enabled ? 'bg-blue-600' : 'bg-gray-300'}`}
                            >
                              <span className={`inline-block h-2 w-2 sm:h-3 sm:w-3 transform rounded-full bg-white shadow transition-transform ${segment.enabled ? 'translate-x-3 sm:translate-x-4' : 'translate-x-0.5'}`} />
                            </button>

                            <div className="sm:hidden flex items-center justify-center">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleSegment(segment.id);
                                }}
                                className={`p-1 rounded transition-colors ${segment.enabled
                                  ? 'text-blue-600 hover:text-blue-800'
                                  : 'text-gray-400 hover:text-gray-600'
                                  }`}
                                title={segment.enabled ? 'Disable' : 'Enable'}
                              >
                                {segment.enabled ? <ToggleRight className="w-6 h-6 sm:w-4 sm:h-4" /> : <ToggleLeft className="w-6 h-6 sm:w-4 sm:h-4" />}

                              </button>
                            </div>
                          </div>
                        </div>

                        <div
                          onClick={() => handleEditSegment(segment)}
                          className="text-xs sm:text-sm text-gray-900 leading-relaxed cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                        >
                          {segment?.content?.split('\n').map((line: any, lineIndex: any, arr: any[]) => (
                            <React.Fragment key={lineIndex}>
                              {line}
                              {lineIndex < arr.length - 1 && <br />}
                            </React.Fragment>
                          ))}
                        </div>

                        {segment?.keywords?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2 sm:mt-3">
                            {segment.keywords.map((keyword: any, keywordIndex: any) => (
                              <span
                                key={keywordIndex}
                                className="bg-gray-100 text-gray-600 px-1 py-0.5 sm:px-1.5 sm:py-0.5 rounded text-[9px] sm:text-[10px] font-medium"
                              >
                                #{keyword}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {filteredSegments.length === 0 && (
                  <div className="py-8 sm:py-12 text-center text-sm text-gray-500">No chunks found.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Chunk Modal */}
      <EditChunkModal
        segment={editingQuestionAnswer ?? null}
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingQuestionAnswer(null);
        }}
        onSave={handleSaveSegment}
      />
    </div>
  );
};

export default DocumentDetail;
